import { UserButton } from '@clerk/clerk-react';
import type { ComponentProps } from 'react';
import { Link2 as LinkIcon, Webhook as WebhookIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { linksDashboardHref } from '@/lib/links-dashboard';

import { Button } from '@/components/ui';

type AccountMenuProps = ComponentProps<typeof UserButton>;

function CreditCardIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 20 20"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="2.5" y="4.5" width="15" height="11" rx="2" />
      <path d="M2.5 8h15" />
      <path d="M5.5 12.5h3" />
    </svg>
  );
}

function KeyIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 20 20"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="7" cy="10" r="2.5" />
      <path d="M9.5 10H17.5" />
      <path d="M14 10V12.5" />
      <path d="M16.5 10V11.5" />
    </svg>
  );
}

export function AccountMenu(props: AccountMenuProps) {
  const navigate = useNavigate();

  if (!import.meta.env.VITE_CLERK_PUBLISHABLE_KEY) {
    return (
      <Button
        variant="unstyled"
        type="button"
        onClick={() => navigate('/settings')}
        className="flex h-9 w-9 items-center justify-center rounded-full border text-[11px] font-semibold"
        style={{ borderColor: 'var(--line)', background: 'var(--paper-sunk)', color: 'var(--ink-mute)' }}
        aria-label="Account settings"
        title="Account settings"
      >
        OK
      </Button>
    );
  }

  return (
    <UserButton {...props}>
      <UserButton.MenuItems>
        <UserButton.Action
          label="Links"
          labelIcon={<LinkIcon size={16} strokeWidth={1.8} />}
          onClick={() => {
            const href = linksDashboardHref();
            if (href.startsWith('/')) {
              navigate(href);
            } else {
              window.location.assign(href);
            }
          }}
        />
        <UserButton.Action
          label="API Keys"
          labelIcon={<KeyIcon />}
          onClick={() => navigate('/settings?tab=api-keys')}
        />
        <UserButton.Action
          label="Webhooks"
          labelIcon={<WebhookIcon size={16} strokeWidth={1.8} />}
          onClick={() => navigate('/settings?tab=webhooks')}
        />
        <UserButton.Action
          label="Billing"
          labelIcon={<CreditCardIcon />}
          onClick={() => navigate('/settings?tab=billing')}
        />
      </UserButton.MenuItems>
    </UserButton>
  );
}
